import React from 'react';
import { Button } from 'components/Button';

/**
 * View / Download for a stock transfer's PO reference document.
 *
 * Both actions route through the same signed-URL helper
 * (`openUniformDocument`) and differ only in the `disposition` the backend
 * stamps on the presigned URL — `inline` opens the file in a tab, `attachment`
 * makes the browser save it. `uniform/documents.py` has supported both from
 * the start; only the inline path was ever wired up on the front end, so
 * there was no way to keep a copy of a supplier's LPO from the list.
 *
 * ## Why `.table-link` and never `.btn`
 *
 * `theme-overrides.css` forces `padding: 0 14px !important` on any `.btn`
 * inside `.ant-table-tbody`, which would push these 14px off the PO number
 * they belong to — the same bug that made record links leave the column's
 * left edge. `.table-link` is the module's chrome-free, brand-red treatment,
 * documented there as working on `<button>` as well as `<a>`, and it carries
 * the `:focus-visible` ring these need: they are ordinary tab stops inside a
 * table row, so the keyboard path has to be visible.
 *
 * ## Naming
 *
 * `compact` is the in-row form: icon-only, so the PO column stays narrow in a
 * grid that already scrolls horizontally. The accessible name then rests
 * entirely on `aria-label`, never on the icon or the `title` tooltip. In the
 * expanded panel there is room for a visible label, and the `aria-label` is a
 * superset of that label (WCAG 2.5.3 Label in Name), so a speech-input user
 * can say the words they can see.
 *
 * @param {object} record A stock transfer row from the list serializer.
 * @param {string|null} openingDoc In-flight request key, `"<id>:<disposition>"`.
 * @param {(record: object, disposition: 'inline'|'attachment') => void} onOpen
 * @param {boolean} [compact] Icon-only form for use inside a table cell.
 */
const TransferDocumentActions = ({ record, openingDoc, onOpen, compact = false }) => {
  if (!record || !record.has_po_document) return null;

  // The document belongs to a PO when there is a number to name it by;
  // otherwise the transfer's own reference is the only handle a screen-reader
  // user has to tell one row's buttons from the next.
  const subject = record.po_number ? `PO ${record.po_number}` : `transfer ${record.reference}`;

  const viewKey = `${record.id}:inline`;
  const downloadKey = `${record.id}:attachment`;

  // Stop propagation on the button itself rather than on a wrapping <div>:
  // a click handler on a non-interactive element would need matching keyboard
  // handlers and a role to satisfy jsx-a11y, for no gain. This keeps the two
  // actions off the row's click path if the row ever becomes clickable or the
  // column ever gains a sorter.
  const run = (disposition) => (e) => {
    e.stopPropagation();
    onOpen(record, disposition);
  };

  // `.table-link` deliberately drops `.btn`, which also drops the
  // `.ant-table-tbody .btn i { font-size: 12px }` rule these icons used to get.
  // In the compact form that would leave them a step heavier than the action
  // buttons beside them, so the size is restored explicitly.
  const style = compact ? { fontSize: 13 } : undefined;

  return (
    <span className="d-inline-flex align-items-center gap-2">
      <Button
        type="button"
        className="table-link"
        style={style}
        icon="fa fa-eye"
        label={compact ? undefined : 'View'}
        aria-label={`View PO document for ${subject}`}
        title="View PO document"
        loading={openingDoc === viewKey}
        disabled={openingDoc === viewKey}
        onClick={run('inline')}
      />
      <Button
        type="button"
        className="table-link"
        style={style}
        icon="fa fa-download"
        label={compact ? undefined : 'Download'}
        aria-label={`Download PO document for ${subject}`}
        title="Download PO document"
        loading={openingDoc === downloadKey}
        disabled={openingDoc === downloadKey}
        onClick={run('attachment')}
      />
    </span>
  );
};

export default TransferDocumentActions;
